import { Tab, Tabs } from "@blueprintjs/core";
import { AgGridReact } from "ag-grid-react";
import { useChromeHeightVar } from "in_queue/hooks/useChromeHeightVar";
import {
    ClusterProjectsBaseScenario,
    useClusterProjectRows
} from "in_queue/hooks/useClusterProjectRows";
import { useCustomUpgradesStatus } from "in_queue/hooks/useCustomUpgradesStatus";
import { ClusterProjectRow } from "in_queue/types/clusterProjectRowType";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loading } from "types/loadingType";
import { segmentTrackOpenHomePage } from "./analytics";
import css from "./ClusterPage.module.scss";
import { ClusterMetricCards } from "./components/cluster/ClusterMetricCards";
import { ClusterStatusBanner } from "./components/cluster/ClusterStatusBanner";
import { SectionHeader } from "./components/common/SectionHeader";
import { CustomUpgradesSection } from "./components/CustomUpgrades/CustomUpgradesSection";
import { SpeculativeProjectsSection } from "./components/SpeculativeProjects/SpeculativeProjectsSection";
import {
    ClusterProjectsExportButton,
    ClusterProjectsTable
} from "./components/tables/ClusterProjectsTable";
import { RibbonSlotContext } from "./components/tables/ribbon/RibbonSlotContext";
import { TableActionRibbon } from "./components/tables/ribbon/TableActionRibbon";
import {
    ProjectFilterCriteria,
    useProjectFilters
} from "./components/tables/ribbon/useProjectFilters";
import { useCluster, useStageConfig } from "./contexts/ClusterContext";
import { getClusterLabel, LegacyClusterInfo } from "./types/clusterType";

const clusterProjectFilterCriteria = (
    row: ClusterProjectRow
): ProjectFilterCriteria => ({
    fuelType: row.fuelType,
    studyGroup: row.studyGroup ?? "",
    isTracked: row.isTracked || row.isFavorited,
    stats: row
});

const TAB_PROJECTS = "projects";
const TAB_STUDY_PROJECTS = "study-projects";
const TAB_CUSTOM_UPGRADES = "custom-upgrades";

const ProjectsTabPanel: React.FC<{
    cluster: LegacyClusterInfo;
    projects: Loading<ClusterProjectRow[]>;
    baseScenario: ClusterProjectsBaseScenario | undefined;
    hasMultipleStudyGroups: boolean;
}> = ({ cluster, projects, baseScenario, hasMultipleStudyGroups }) => {
    const gridRef = useRef<AgGridReact<ClusterProjectRow>>(null);
    const allRows = useMemo(
        () => (projects === "loading" ? [] : projects),
        [projects]
    );
    const filters = useProjectFilters({
        tableName: "cluster_projects",
        rows: allRows,
        filterCriteria: clusterProjectFilterCriteria,
        region: cluster.region,
        showStudyGroup: hasMultipleStudyGroups,
        labels: { studyGroup: "Group", tracked: "Tracked" }
    });

    return (
        <div className={css["ClusterPage-projects-panel"]}>
            <TableActionRibbon
                sticky
                leftActions={[...filters.actions]}
                rightActions={[
                    <ClusterProjectsExportButton
                        key="export"
                        gridRef={gridRef}
                        cluster={cluster}
                        disabled={projects === "loading"}
                    />
                ]}
            />
            <ClusterProjectsTable
                cluster={cluster}
                projects={projects}
                rowFilter={filters.rowFilter}
                baseScenario={baseScenario}
                showStudyGroup={hasMultipleStudyGroups}
                gridRef={gridRef}
                quickFilterText={filters.filterText}
                stickyHeader
            />
        </div>
    );
};

export const ClusterPage: React.FC = () => {
    const cluster = useCluster();
    const { enableSpeculativeProjects } = useStageConfig();
    const customUpgradesStatus = useCustomUpgradesStatus();
    const { rows, baseScenario, hasMultipleStudyGroups } =
        useClusterProjectRows();
    const [selectedTabId, setSelectedTabId] = useState(TAB_PROJECTS);

    // Which tabs a cluster offers varies by region and subscription, so a tab
    // left open on the previous cluster may not exist on this one.
    const availableTabIds = useMemo(
        () => [
            TAB_PROJECTS,
            ...(enableSpeculativeProjects ? [TAB_STUDY_PROJECTS] : []),
            ...(customUpgradesStatus !== "hidden" ? [TAB_CUSTOM_UPGRADES] : [])
        ],
        [enableSpeculativeProjects, customUpgradesStatus]
    );

    useEffect(() => {
        if (!availableTabIds.includes(selectedTabId)) {
            setSelectedTabId(TAB_PROJECTS);
        }
    }, [availableTabIds, selectedTabId]);

    const hostRef = useRef<HTMLDivElement | null>(null);
    const chromeRef = useRef<HTMLDivElement | null>(null);
    // A callback ref captured into state, so the provider re-renders once the
    // slot element exists for the ribbon to portal into.
    const [ribbonSlot, setRibbonSlot] = useState<HTMLDivElement | null>(null);
    const ribbonSlotRef = useCallback(
        (node: HTMLDivElement | null) => setRibbonSlot(node),
        []
    );

    // Publishes the chrome's height as `--iq-ag-header-top`, which a sticky
    // grid header reads so it pins flush below the nav and ribbon.
    useChromeHeightVar(hostRef, chromeRef, true);

    useEffect(() => {
        segmentTrackOpenHomePage();
    }, []);

    const panelProps = { cluster, baseScenario, hasMultipleStudyGroups };

    return (
        <div className={css["ClusterPage-container"]}>
            <SectionHeader level="h1" title={getClusterLabel(cluster)} />
            <ClusterStatusBanner />
            <ClusterMetricCards rows={rows} baseScenario={baseScenario} />
            <div ref={hostRef}>
                <div ref={chromeRef} className={css["ClusterPage-chrome"]}>
                    <Tabs
                        selectedTabId={selectedTabId}
                        onChange={(newTabId) =>
                            setSelectedTabId(String(newTabId))
                        }
                        size="large"
                    >
                        <Tab id={TAB_PROJECTS} title="Projects" />
                        {enableSpeculativeProjects && (
                            <Tab
                                id={TAB_STUDY_PROJECTS}
                                title="Study Projects"
                            />
                        )}
                        {customUpgradesStatus !== "hidden" && (
                            <Tab
                                id={TAB_CUSTOM_UPGRADES}
                                title="Custom Upgrades"
                            />
                        )}
                    </Tabs>
                    <div
                        ref={ribbonSlotRef}
                        className={css["ClusterPage-ribbon-slot"]}
                    />
                </div>
                <RibbonSlotContext.Provider
                    value={selectedTabId === TAB_PROJECTS ? ribbonSlot : null}
                >
                    {selectedTabId === TAB_PROJECTS && (
                        // Keyed on the cluster so the search box and filters
                        // reset rather than carrying a previous cluster's fuel
                        // types into one that has none of them.
                        <ProjectsTabPanel
                            key={cluster.id}
                            projects={rows}
                            {...panelProps}
                        />
                    )}
                </RibbonSlotContext.Provider>
                {selectedTabId === TAB_STUDY_PROJECTS && (
                    <SpeculativeProjectsSection />
                )}
                {selectedTabId === TAB_CUSTOM_UPGRADES && (
                    <CustomUpgradesSection />
                )}
            </div>
        </div>
    );
};
